import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';
import {TaskModal} from 'views/module';

import TaskItem from './components/TaskItem';
import TaskListSkeleton from './components/TaskListSkeleton';

import backend from 'utils/backend';
import moment from 'moment';

const MyDay = (props) => {
  const {
    user,
    taskRefreshSetMyTasksCount,
    taskRefreshIncrementMyTasksCount,
    taskRefreshDecrementMyTasksCount,
    statusProgressWrap: progressWrap,
  } = props;

  const [todaysTasks, setTodaysTasks] = React.useState([]);
  const [dueTodayCount, setDueTodayCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [model, setModel] = React.useState({});

  const loadTasks = React.useCallback(() => {
    if (!user?.userRn) {
      setTodaysTasks([]);
      setDueTodayCount(0);
      taskRefreshSetMyTasksCount(0);
      return;
    }

    setIsLoading(true);

    progressWrap(async () => {
      try {
        const oneWeekAgoUtc = moment().startOf('day').subtract(1, 'week').utc();
        const endOfTodayUtc = moment().endOf('day').utc();
        const startDate = Math.floor(new Date(oneWeekAgoUtc.toISOString()).getTime());
        const endOfTodayDate = Math.floor(new Date(endOfTodayUtc.toISOString()).getTime());
        const taskSortBy = {sortKey: 'dueDate', sortDirection: 'ASC'};
        const userTaskFilters = [
          {key: 'resourceRn', value: user.userRn},
          {key: 'dueDate', value: `[${startDate} TO ${endOfTodayDate}]`},
          {key: 'status', value: ['OPEN', 'IN_PROGRESS', 'COMPLETE']},
        ];
        let allUserTasks = [];
        let lastEvaluatedKey = null;

        while (lastEvaluatedKey !== undefined) {
          const userTaskResponse = await backend.workflowEngine.queryResourceTasks(userTaskFilters, taskSortBy, lastEvaluatedKey);

          allUserTasks = [...allUserTasks, ...(userTaskResponse?.list || [])];
          lastEvaluatedKey = userTaskResponse?.lastEvaluatedKey;

          if (!lastEvaluatedKey) {
            break;
          }
        }

        const userTasks = allUserTasks.filter((task) =>
          !(task.status === 'COMPLETE' && moment(task.dueDate).isBefore(moment().startOf('day').utc())),
        );
        const myTasksCount = userTasks.filter((task) => task.status !== 'COMPLETE').length;

        setTodaysTasks(userTasks);
        setDueTodayCount(myTasksCount);
        taskRefreshSetMyTasksCount(myTasksCount);
      } finally {
        setIsLoading(false);
      }
    });
  }, [progressWrap, taskRefreshSetMyTasksCount, user?.userRn]);

  const hydrateTaskConnections = React.useCallback(async (taskData) => {
    const connectionRns = taskData?.connections || [];

    const connectionInfo = await Promise.all(connectionRns.map(async (connectionRn) => {
      if (/^RN:CLIENT-V1-[^:]+$/.test(connectionRn)) {
        try {
          const clientResponse = await backend.client.getClient(connectionRn, false);
          const clientDetail = clientResponse?.clientDetail;

          const [firstName, ...lastNameParts] = clientDetail?.clientName.split(' ');

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      if (/^RN:CAREGIVER-V1-[^:]+$/.test(connectionRn)) {
        try {
          const caregiverResponse = await backend.caregiver.getCaregiver(connectionRn, false);
          const caregiverDetail = caregiverResponse?.caregiverDetail;

          const caregiverName = caregiverDetail?.caregiverName || '';
          const [firstName = '', ...lastNameParts] = caregiverName.trim().split(/\s+/);

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      return null;
    }));

    return {
      ...taskData,
      connectionInfo: connectionInfo.filter(Boolean),
    };
  }, []);

  const handleSetStatusToComplete = React.useCallback((taskRn, isComplete) => {
    if (isComplete) return;

    const currentTask = todaysTasks.find((task) => task.taskRn === taskRn);

    if (!currentTask) {
      return;
    }

    const completedTask = {
      ...currentTask,
      status: 'COMPLETE',
    };

    setTodaysTasks((previousTasks) =>
      previousTasks.map((task) => task.taskRn === taskRn ? completedTask : task),
    );
    setDueTodayCount((previousCount) => Math.max(0, previousCount - 1));
    taskRefreshDecrementMyTasksCount();
    setIsLoading(true);

    progressWrap(async () => {
      try {
        await backend.workflowEngine.updateTaskStatus(taskRn, 'COMPLETE');
      } finally {
        setIsLoading(false);
      }
    });
  }, [progressWrap, taskRefreshDecrementMyTasksCount, todaysTasks]);

  React.useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleAddTask = (taskData) => {
    progressWrap(async () => {
      try {
        const createdTask = await backend.workflowEngine.addTask(taskData);
        const taskWithInfo = await hydrateTaskConnections(createdTask);

        setTodaysTasks((previousTasks) =>
          [...previousTasks, taskWithInfo].sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf()),
        );
        if (createdTask.status !== 'COMPLETE') {
          setDueTodayCount((previousCount) => previousCount + 1);
          taskRefreshIncrementMyTasksCount();
        }
      } finally {
        setModel({});
      }
    });
  };

  const openDialogs = {
    close: () => {
      setModel({});
    },
    addTask: () => {
      setModel({action: 'ADD_TASK'});
    },
    editTask: (task) => {
      setModel({action: 'EDIT_TASK', task});
    },
  };

  const edits = {
    editTask: (taskRn, taskData) => {
      const currentTask = todaysTasks.find((task) => task.taskRn === taskRn);

      progressWrap(async () => {
        try {
          const updatedTaskResponse = await backend.workflowEngine.updateTask(taskRn, taskData);
          const updatedTask = await hydrateTaskConnections(updatedTaskResponse);
          setTodaysTasks((previousTasks) => {
            const filteredTasks = previousTasks.filter((task) => task.taskRn !== taskRn);

            return [...filteredTasks, updatedTask].sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());
          });

          if (currentTask?.status !== 'COMPLETE' && updatedTask.status === 'COMPLETE') {
            setDueTodayCount((previousCount) => Math.max(0, previousCount - 1));
            taskRefreshDecrementMyTasksCount();
          }
          if (currentTask?.status === 'COMPLETE' && updatedTask.status !== 'COMPLETE') {
            setDueTodayCount((previousCount) => previousCount + 1);
            taskRefreshIncrementMyTasksCount();
          }
        } finally {
          setModel({});
        }
      });
    },
    removeTask: (taskData) => {
      setTodaysTasks((previousTasks) => previousTasks.filter((task) => task.taskRn !== taskData.taskRn));
      if (taskData.status !== 'COMPLETE') {
        setDueTodayCount((previousCount) => Math.max(0, previousCount - 1));
        taskRefreshDecrementMyTasksCount();
      }

      progressWrap(async () => {
        try {
          await backend.workflowEngine.removeTask(taskData.taskRn);
        } finally {
          setModel({});
        }
      });
    },
  };

  const popModelGenerator = () => {
    let popModel = null;

    switch (model.action) {
      case 'ADD_TASK':
        popModel = (
          <TaskModal
            mode='add'
            onClose={openDialogs.close}
            addTask={handleAddTask}
            askConnections
          />
        );
        break;
      case 'EDIT_TASK':
        popModel = (
          <TaskModal
            mode='edit'
            onClose={openDialogs.close}
            editTask={edits.editTask}
            task={model.task}
            askStatus
            askConnections
          />
        );
        break;
    }

    return popModel;
  };

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 4,
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: '100%',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            {translate('tasks.MyDay.Header.Greeting', {userFirstName: user.firstName})}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {translate('tasks.MyDay.Header.Summary', {todayDate: moment().format('dddd MMM DD'), numTasks: dueTodayCount})}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={1.5}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontSize: '16px',
            fontWeight: 700,
            color: 'grey.500',
          }}
        >
          {translate('tasks.MyDay.Sections.NeedsYou')}
        </Typography>
        <Stack
          spacing={1.5}
        >
          {
            isLoading && (
              <TaskListSkeleton />
            )
          }
          {
            !isLoading && todaysTasks.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {translate('tasks.MyDay.Empty')}
              </Typography>
            )
          }
          {
            !isLoading && todaysTasks.map((task) => (
              <TaskItem
                key={task.taskRn}
                task={task}
                handleSetStatusToComplete={handleSetStatusToComplete}
                onClickEditTask={openDialogs.editTask}
                removeTask={edits.removeTask}
              />
            ))
          }
          <Box
            component='button'
            onClick={openDialogs.addTask}
            sx={{
              background: 'none',
              border: 'none',
              color: '#A8A49E',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '14px',
              p: 0,
              mt: 1,
            }}
          >
            <Box component='span'>+</Box>
            <Translate expression='tasks.Common.NewTask' />
          </Box>
        </Stack>
      </Stack>
      {popModelGenerator()}
    </Stack>
  );
};

MyDay.propTypes = {
  user: PropTypes.object,
  taskRefresh: PropTypes.number,
  taskRefreshSetMyTasksCount: PropTypes.func.isRequired,
  taskRefreshIncrementMyTasksCount: PropTypes.func.isRequired,
  taskRefreshDecrementMyTasksCount: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(MyDay));
